<template>
    <li class="h-fit border-l border-(--border-subtle) py-sm" :class="{ 'py-xs': tight }">
        <!-- Parent Item -->
        <div class="flex gap-xs">
            <button
                class="visible-on-hover ml-sm flex h-8 w-full items-center p-0"
                :class="{
                    'has-children': item.children.length > 0,
                    expanded: isExpanded(item),
                }"
                tabindex="0"
                @click.prevent="(e) => toggle(e, item)"
                @keydown.enter.prevent="(e) => toggle(e, item)"
                @keydown.space.prevent="(e) => toggle(e, item)"
            >
                <div
                    class="pl-s flex h-full w-full items-center gap-sm rounded-xsm transition-colors duration-300 ease-linear hover:bg-(--surface-highlight)"
                    :class="{
                        'border-l-2 border-(--border-focus) bg-(--surface-highlight)': isExpanded(item),
                    }"
                >
                    <i
                        v-if="item.children.length > 0"
                        class="i-dropdown-arrow block transition-transform duration-300"
                        :class="{ rotated: isExpanded(item) }"
                    ></i>
                    <span v-else></span>
                    <i :class="`i-${item.locationType}`" class="h-5 w-5"></i>

                    <template v-if="links">
                        <router-link
                            v-if="
                                organizationStore.user?.organizations
                                    .find(
                                        (org) =>
                                            org.organization_id ===
                                            organizationStore.usersOrganization?.organization_id,
                                    )
                                    ?.organization_roles.some((role: SimpleRole) => role.name === 'org-admin')
                            "
                            class="custom-link"
                            :class="{
                                'text-(--link-default)': isChecked(item),
                                'hover:underline': links,
                            }"
                            :to="{
                                name: 'location-details',
                                params: { locationId: item.id },
                            }"
                            tabindex="0"
                            @keydown.stop
                        >
                            {{ item.name }}
                        </router-link>
                        <p v-else class="custom-link" :class="{ 'text-(--link-default)': isChecked(item) }">
                            {{ item.name }}
                        </p>
                    </template>
                    <p
                        v-else
                        :class="[plain ? 'plain-label' : 'custom-link', { 'text-(--link-default)': !plain && isChecked(item) }]"
                    >
                        {{ item.name }}
                    </p>
                    <div class="ml-auto flex gap-sm pr-sm">
                        <p class="font-matter visible-toggle-item font-normal text-(--text-muted)">
                            {{ item.locationType }}
                        </p>
                    </div>
                </div>
            </button>
            <input
                v-if="editable || showUneditable"
                type="checkbox"
                :checked="isChecked(item)"
                :disabled="!editable || stickyItems?.has(item.id) || isNonSelectable"
                :title="isNonSelectable ? 'Select a more specific location within this one' : undefined"
                tabindex="0"
                class="focus:outline-hidden rounded border hover:border-(--border-focus) focus:border-(--border-focus) focus:ring-2 focus:ring-(--border-focus) disabled:cursor-not-allowed disabled:opacity-40"
                @click="checked(item)"
                @keydown.enter.prevent="checked(item)"
                @keydown.space.prevent="checked(item)"
            />
        </div>

        <!-- Recursive Children -->

        <ul v-if="isExpanded(item)" class="ml-[42px]">
            <CascadingUuidItem
                v-for="child in [...item.children].sort((a, b) => a.name.localeCompare(b.name))"
                :key="child.id"
                :item="child"
                :is-expanded="isExpanded"
                :toggle="toggle"
                :checked="checked"
                :is-checked="isChecked"
                :editable="editable"
                :sticky-items="stickyItems"
                :show-uneditable
                :non-selectable-types="nonSelectableTypes"
                class="border-left border-(--border-strong)"
                :tight="tight"
                :links="links"
                :plain="plain"
            />
        </ul>
    </li>
</template>
<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useOrganizationStore } from '@/stores/organization';
import type { SimpleRole, UUIDLocation } from '@/stores/store-interfaces';

const organizationStore = useOrganizationStore();

// Props for each item
const props = defineProps({
    item: {
        type: Object as () => UUIDLocation,
        required: true,
    },
    isExpanded: {
        type: Function,
        required: true,
    },
    toggle: {
        type: Function,
        required: true,
    },
    checked: {
        type: Function,
        required: true,
    },
    isChecked: {
        type: Function,
        required: true,
    },
    editable: {
        type: Boolean,
        required: true,
    },
    showUneditable: {
        type: Boolean,
        default: false,
    },
    tight: {
        type: Boolean,
        default: false,
    },
    stickyItems: {
        type: Object as PropType<Set<string>>,
        required: false,
    },
    links: {
        type: Boolean,
        default: false,
    },
    // Read-only display: render labels as plain text (no link underline, no selected-blue), for
    // contexts like the incident affected-locations tree where the nodes aren't navigable.
    plain: {
        type: Boolean,
        default: false,
    },
    // Location types whose checkbox is disabled (e.g. `region` in the incident footprint picker,
    // where a country on its own is too broad — see ITDEVELOP-2987). The node stays expandable so
    // the user can drill down to a selectable child.
    nonSelectableTypes: {
        type: Array as PropType<string[]>,
        default: () => [],
    },
});

const isNonSelectable = computed(() => props.nonSelectableTypes.includes(props.item.locationType));
</script>

<style scoped>
@reference '@/assets/styles/default.css';
.i-dropdown-arrow {
    transition: transform 0.3s ease;
}

.i-dropdown-arrow.rotated {
    transform: rotate(180deg);
}

.visible-on-hover {
    .visible-toggle-item {
        display: none;
    }

    &:hover,
    &:focus {
        .visible-toggle-item {
            display: block;
        }
    }
}

.custom-link {
    font-weight: 400;
    font-family: 'Matter-Regular';
    @apply text-md hover:underline;
}

.plain-label {
    font-weight: 400;
    font-family: 'Matter-Regular';
    @apply text-md;
}
</style>
